import { Box, Button, Modal, SelectInput, toast } from '@palmetto/palmetto-components';
import { FormikValues, useFormik } from 'formik';
import { SyntheticEvent } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useCreateOrganizationPpwModifiersMutation, useGetModifiersQuery } from '../../../../../../services/pricing';
import { useGetAdminUtilitiesQuery } from '../../../../../../services/utilities';

export const CreatePpwModifier = ({
  isModalOpen,
  closeModal,
}: {
  isModalOpen: boolean;
  closeModal: (event?: SyntheticEvent) => void;
}) => {
  const [createOrganizationPpwModifiers] = useCreateOrganizationPpwModifiersMutation();
  const { alias = '', programType = '' } = useParams<{ alias: string; programType: string }>();
  const { data: ppwModifiers = [] } = useGetModifiersQuery({
    type: 'ppwAdjustment',
    programType,
  });
  const filter = [{ id: 'programType', operation: 'Equal', selectedValues: [programType] }];
  const { data: utilities = [] } = useGetAdminUtilitiesQuery({
    advancedFilters: JSON.stringify(filter),
  });
  const formik = useFormik({
    initialValues: {
      ppwModifier: {},
      state: [],
    },
    validationSchema: yup.object({
      ppwModifier: yup.object().required('PPW Modifier is required'),
    }),
    onSubmit: async (values: FormikValues, { setSubmitting, resetForm }) => {
      try {
        const states = values.state.map((state: { label: string; value: string }) => state.value);
        await createOrganizationPpwModifiers({
          alias,
          data: { ppwModifierId: values.ppwModifier.value, states, programType },
        }).unwrap();
        toast.success('PPW Modifier added successfully');
        resetForm();
        closeModal();
      } catch (e) {
        console.error(e);
        toast.error('Error adding PPW Modifier');
      } finally {
        setSubmitting(false);
      }
    },
  });

  const stateUtilityMapping = utilities.reduce(
    (acc, utility) => {
      acc[utility.state] = acc[utility.state] ?? [];
      acc[utility.state].push(utility);
      return acc;
    },
    {} as Record<string, any[]>,
  );

  const stateOptions = Object.keys(stateUtilityMapping)
    .toSorted()
    .map((state) => ({ label: state, value: state }));

  return (
    <Modal ariaLabelledBy="cratePpwModifier" isOpen={isModalOpen} onDismiss={closeModal} maxWidth="500px">
      <Modal.Header id="createPpwModifier" title="Add PPW Modifier" onDismiss={closeModal} />
      <form onSubmit={formik.handleSubmit}>
        <Modal.Body background="secondary">
          <Box direction="column" childGap="xl">
            {ppwModifiers && (
              <SelectInput
                label="PPW Modifier"
                name="ppwModifier"
                id="ppwModifier"
                value={formik.values.ppwModifier}
                onChange={(event) => formik.setFieldValue('ppwModifier', event.target.value)}
                options={ppwModifiers.map((ppwModifier: any) => ({
                  label: ppwModifier.name,
                  value: ppwModifier.id,
                }))}
                isRequired
                onBlur={formik.handleBlur}
                error={formik.touched.ppwModifier && (formik.errors.ppwModifier as string)}
                menuPortalTarget={document.body}
              />
            )}
            <Box>
              <SelectInput
                id="state"
                name="state"
                label="State"
                value={formik.values.state}
                onChange={(event) => {
                  formik.setFieldValue('state', event.target.value);
                }}
                options={stateOptions}
                onBlur={formik.handleBlur}
                isDisabled={formik.isSubmitting}
                error={formik.touched.state && (formik.errors.state as string)}
                menuPortalTarget={document.body}
                isMulti
              />
            </Box>
          </Box>
        </Modal.Body>
        <Modal.Footer>
          <Button
            as="button"
            onClick={() => {
              formik.resetForm();
              closeModal();
            }}
            variant="secondary"
            tone="neutral"
            size="md"
            isLoading={formik.isSubmitting}
          >
            Cancel
          </Button>
          <Button variant="primary" size="md" type="submit" isLoading={formik.isSubmitting}>
            Add PPW Modifier
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
